// iter417 — useDashboardLayout hook.
// Encapsulates widget visibility + grid layout state + the back-end
// persistence effect chain so the dashboard page doesn't manage them
// inline. Three concerns owned here:
//   • `widgets`  — boolean map of which widgets render (per-user pref)
//   • `layout`   — react-grid-layout config (per-user pref)
//   • Hydrate from /dashboard/layout on mount; mirror to localStorage
//     immediately, debounce-write to backend on change.
//
// Returns the same handler names the page used to declare locally so
// the call sites need no rewiring beyond replacing 4 useStates and 4
// inline fns with a single hook destructure.
import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '@/lib/api';
import {
  DEFAULT_WIDGETS,
} from '@/components/dashboard/dashboardConstants';
import {
  DEFAULT_LAYOUT,
  getWidgetPrefs, saveWidgetPrefs,
  getLayoutPrefs, saveLayoutPrefs,
} from '@/components/dashboard/dashboardLayoutHelpers';

export function useDashboardLayout({ tr } = {}) {
  const [widgets, setWidgets] = useState(getWidgetPrefs());
  const [layout, setLayout] = useState(getLayoutPrefs());

  // Hydrate from backend on mount. Local prefs win for the first paint
  // (so the page never flashes the default skin) — backend prefs merge
  // in once the request resolves.
  useEffect(() => {
    api.get('/dashboard/layout').then((res) => {
      if (res.data?.widgets) {
        const merged = { ...DEFAULT_WIDGETS, ...res.data.widgets };
        setWidgets(merged);
        saveWidgetPrefs(merged);
      }
      if (res.data?.layout) {
        setLayout(res.data.layout);
        saveLayoutPrefs(res.data.layout);
      }
    }).catch(() => { /* offline-OK */ });
  }, []);

  const toggleWidget = useCallback((key) => {
    setWidgets((prev) => {
      const updated = { ...prev, [key]: !prev[key] };
      saveWidgetPrefs(updated);
      api.post('/dashboard/layout', { widgets: updated }).catch(() => {});
      return updated;
    });
  }, []);

  const handleLayoutChange = useCallback((newLayout) => {
    setLayout(newLayout);
    saveLayoutPrefs(newLayout);
    api.post('/dashboard/layout', { layout: newLayout }).catch(() => {});
  }, []);

  const resetLayout = useCallback(() => {
    setLayout(DEFAULT_LAYOUT);
    saveLayoutPrefs(DEFAULT_LAYOUT);
    api.post('/dashboard/layout', { layout: DEFAULT_LAYOUT }).catch(() => {});
    toast.success(tr?.('reset_layout') || 'Layout reset');
  }, [tr]);

  return {
    widgets, setWidgets,
    layout, setLayout,
    toggleWidget,
    handleLayoutChange,
    resetLayout,
  };
}

export default useDashboardLayout;
